import { callGet } from '@/api/fetcher';

/** Room for a ` (N)` collision suffix: 5 characters covers ` (1)` through ` (99)`. */
export const NAME_SUFFIX_BUDGET = 5;

/** Mirrors the backend's `sanitizedName` before it measures length. NFKC can grow a string. */
export const normalizeForLengthCheck = (value: string): string =>
  value.normalize('NFKC').replace(/\s+/g, ' ').trim();

/** Truncates to `maxLength` UTF-16 code units without splitting a character. */
export const truncateWholeChars = (value: string, maxLength: number): string => {
  let result = '';
  for (const char of value) {
    if (result.length + char.length > maxLength) {
      break;
    }
    result += char;
  }
  return result;
};

/** Normalises, then truncates to a length the backend will still accept. */
export const truncateToServerLimit = (value: string, maxLength: number): string =>
  truncateWholeChars(normalizeForLengthCheck(value), maxLength);

/** Pins the check to one account, for flows that must not follow an account switch. */
type ScopedOptions = { accountSlug: string };

/** Calls a `check_name` endpoint and returns its `{ available, count, suggested_name }` body. */
export async function checkNameAvailability(
  endpoint: string,
  scoped?: ScopedOptions
): Promise<{ available: boolean; count: number; suggested_name?: string }> {
  const data = await callGet(endpoint, {}, scoped);

  return {
    available: data.available ?? false,
    count: data.count ?? 0,
    suggested_name: data.suggested_name,
  };
}

/**
 * Calls check_name and returns either the base name (if available) or the
 * backend-suggested alternative.
 */
export const resolveAvailableName = async (
  endpoint: string,
  baseName: string,
  scoped?: ScopedOptions
): Promise<string> => {
  const { available, suggested_name } = await checkNameAvailability(endpoint, scoped);
  return available ? baseName : suggested_name || baseName;
};
